import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";

export interface WarehouseRecord {
  id: ID;
  name: string;
  country: string;
  state: string;
  city: string;
  address: string;
  isActive: boolean;
  createdAt: string;
}

const LS_KEY = "inventory.warehouses.v1";

type WarehouseApiRecord = {
  id: number | string;
  name: string;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  address?: string | null;
  is_active?: boolean | number | null;
  created_at?: string | null;
};

type ListWarehousesApiResponse = {
  data?: WarehouseApiRecord[];
  meta?: {
    page?: number;
    per_page?: number;
    total?: number;
  };
};

type UpsertWarehouseApiResponse = {
  message?: string;
  data?: WarehouseApiRecord;
};

type DeleteWarehouseApiResponse = {
  message?: string;
};

type UpsertWarehouseResult = {
  record: WarehouseRecord;
  message: string;
};

type DeleteWarehouseResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: WarehouseRecord[]): WarehouseRecord[] {
  const byId = new Map<ID, WarehouseRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function mapApiRecord(record: WarehouseApiRecord): WarehouseRecord {
  const active =
    record.is_active === true ||
    record.is_active === 1 ||
    record.is_active === "1" ||
    record.is_active === "true";
  return {
    id: String(record.id),
    name: String(record.name ?? ""),
    country: record.country == null ? "" : String(record.country),
    state: record.state == null ? "" : String(record.state),
    city: record.city == null ? "" : String(record.city),
    address: record.address == null ? "" : String(record.address),
    isActive: active,
    createdAt: String(record.created_at ?? new Date().toISOString()),
  };
}

function load(): WarehouseRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as WarehouseRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: WarehouseRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listWarehouseRecords(): WarehouseRecord[] {
  return load().sort((a, b) => a.name.localeCompare(b.name));
}

export function getWarehouseById(id: ID): WarehouseRecord | undefined {
  return load().find((w) => w.id === id);
}

export type SyncWarehousesOptions = {
  /** When true, only locations marked active (for product pickers). */
  activeOnly?: boolean;
};

export async function syncWarehousesFromApi(options?: SyncWarehousesOptions) {
  const response = await api.get<ListWarehousesApiResponse>("/api/v1/catalog/ware-houses", {
    params: {
      page: 1,
      per_page: 200,
      ...(options?.activeOnly ? { is_active: true } : {}),
    },
  });
  const records = (response.data?.data ?? []).map((r) => mapApiRecord(r));
  save(records);
  return listWarehouseRecords();
}

export async function createWarehouse(data: {
  name: string;
  country?: string;
  state?: string;
  city?: string;
  address?: string;
  isActive?: boolean;
}): Promise<UpsertWarehouseResult> {
  const items = load();
  const payload = {
    name: data.name.trim(),
    country: data.country?.trim() || null,
    state: data.state?.trim() || null,
    city: data.city?.trim() || null,
    address: data.address?.trim() || null,
    is_active: data.isActive ?? true,
  };
  const response = await api.post<UpsertWarehouseApiResponse>("/api/v1/catalog/ware-houses", payload);
  const created = response.data?.data;
  if (!created) {
    throw new Error("Invalid create location response.");
  }
  const record = mapApiRecord(created);
  const existingIndex = items.findIndex((item) => item.id === record.id);
  if (existingIndex >= 0) {
    items[existingIndex] = record;
  } else {
    items.push(record);
  }
  save(items);
  return {
    record,
    message: response.data?.message || "Location created successfully.",
  };
}

export async function updateWarehouse(
  id: ID,
  data: {
    name: string;
    country?: string;
    state?: string;
    city?: string;
    address?: string;
    isActive?: boolean;
  },
): Promise<UpsertWarehouseResult | null> {
  const items = load();
  const i = items.findIndex((w) => w.id === id);
  if (i === -1) return null;
  const payload = {
    name: data.name.trim(),
    country: data.country?.trim() || null,
    state: data.state?.trim() || null,
    city: data.city?.trim() || null,
    address: data.address?.trim() || null,
    is_active: data.isActive ?? items[i].isActive,
  };
  const response = await api.put<UpsertWarehouseApiResponse>(`/api/v1/catalog/ware-houses/${id}`, payload);
  const updated = response.data?.data;
  if (!updated) {
    throw new Error("Invalid update location response.");
  }
  items[i] = mapApiRecord(updated);
  save(items);
  return {
    record: items[i],
    message: response.data?.message || "Location updated successfully.",
  };
}

export async function deleteWarehouse(id: ID): Promise<DeleteWarehouseResult> {
  const response = await api.delete<DeleteWarehouseApiResponse>(`/api/v1/catalog/ware-houses/${id}`);
  const items = load();
  const nextItems = items.filter((w) => w.id !== id);
  const deleted = nextItems.length !== items.length;
  if (deleted) {
    save(nextItems);
  }
  return {
    success: deleted,
    message: response.data?.message || "Location deleted successfully.",
  };
}
